import {
  OnDemandPeriod,
  OnDemandRequestStatus,
  type OnDemandContract,
  type OnDemandContractBlockReason,
  type OnDemandContractRule,
} from '@/integrations/agreements/client';

/**
 * Regras da coleta sob demanda para exibição. Quem decide é o musa-agreements, na criação —
 * aqui só se evita oferecer uma data que voltaria recusada.
 */

export const OPERATION_TIMEZONE = 'America/Sao_Paulo';

/** `YYYY-MM-DD` do instante no fuso da operação. */
export function dayInOperationTimezone(date: Date): string {
  // en-CA formata como YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: OPERATION_TIMEZONE }).format(date);
}

/** `YYYY-MM-DD` + `days` dias de calendário, sem passar pelo fuso do navegador. */
export function addDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date + days)).toISOString().slice(0, 10);
}

/** O que importa da regra para a data: coleta e caçamba usam o mesmo cálculo. */
type AdvanceRule = Pick<OnDemandContractRule, 'minAdvanceValue' | 'minAdvanceUnit'>;

/**
 * Antecedência em dias inteiros (MC-377): menos de 24h só existe com o pedido no mesmo dia, que é
 * outra configuração da regra. Regra antiga ainda em horas arredonda para cima — 6h ou 24h viram
 * 1 dia, 36h viram 2 — para a tela nunca oferecer uma data que a regra em dias recusaria.
 */
export function advanceDays(rule: AdvanceRule): number {
  if (rule.minAdvanceUnit === 'DAYS') return rule.minAdvanceValue;
  return Math.ceil(rule.minAdvanceValue / 24);
}

/**
 * Primeiro dia que a antecedência permite: hoje em São Paulo + a antecedência em dias. Sem regra
 * (contrato anterior às regras), só não pode data passada.
 */
export function minAllowedDate(rule: AdvanceRule | null, now: Date = new Date()): string {
  const today = dayInOperationTimezone(now);
  return rule ? addDays(today, advanceDays(rule)) : today;
}

export function describeAdvance(rule: AdvanceRule | null): string {
  const days = rule ? advanceDays(rule) : 0;
  if (days <= 0) return 'Sem antecedência mínima';

  return `Antecedência mínima de ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

/** "2 de 4 coletas usadas no mês", ou "Sem limite mensal". */
export function describeMonthlyUsage(contract: Pick<OnDemandContract, 'rule' | 'used'>): string {
  const limit = contract.rule?.monthlyLimit;

  if (limit == null) return 'Sem limite mensal';

  return `${contract.used} de ${limit} ${limit === 1 ? 'coleta usada' : 'coletas usadas'} no mês`;
}

export const BLOCK_REASON_LABELS: Record<OnDemandContractBlockReason, string> = {
  BUCKET_PACKAGING: 'Mov. Equipamento',
  NOT_CONTRACTED: 'Não contratado',
  MONTHLY_LIMIT_REACHED: 'Limite do mês atingido',
};

/** `YYYY-MM` → `MM/yyyy`. */
const formatMonth = (month: string) => {
  const [year, m] = month.split('-');
  return `${m}/${year}`;
};

/**
 * Por que o contrato não aceita pedido agora. "Não contratado" (limite 0) e "limite do mês
 * atingido" são mensagens distintas de propósito: no primeiro caso o serviço precisa ser
 * contratado; no segundo ele está contratado e o mês acabou.
 */
export function describeBlockReason(
  contract: Pick<OnDemandContract, 'blockReason' | 'rule' | 'used' | 'month'>,
): string | null {
  switch (contract.blockReason) {
    case 'BUCKET_PACKAGING':
      return 'Caçamba se pede como movimentação de equipamento: escolha "Movimentação de Equipamento" no topo desta tela para pedir colocação, retirada ou troca.';
    case 'NOT_CONTRACTED':
      return 'O serviço sob demanda não está contratado neste contrato. Fale com a Musa para contratar.';
    case 'MONTHLY_LIMIT_REACHED':
      return `O limite de coletas sob demanda de ${formatMonth(contract.month)} já foi usado: `
        + `${contract.used} de ${contract.rule?.monthlyLimit ?? contract.used}. Fale com a Musa para ampliar o contrato.`;
    default:
      return null;
  }
}

export const REQUEST_STATUS_DISPLAY: Record<
  OnDemandRequestStatus,
  { label: string; variant: 'info' | 'success' | 'destructive' | 'secondary' }
> = {
  [OnDemandRequestStatus.PENDING]: { label: 'Pendente', variant: 'info' },
  [OnDemandRequestStatus.ACCEPTED]: { label: 'Aceita', variant: 'success' },
  [OnDemandRequestStatus.REJECTED]: { label: 'Recusada', variant: 'destructive' },
  [OnDemandRequestStatus.CANCELED]: { label: 'Cancelada', variant: 'secondary' },
};

export const PERIOD_LABELS: Record<OnDemandPeriod, string> = {
  [OnDemandPeriod.DIURNO]: 'Diurno',
  [OnDemandPeriod.NOTURNO]: 'Noturno',
};

/** `YYYY-MM-DD` → `dd/MM/yyyy`, sem passar por `Date` (que deslocaria o dia pelo fuso). */
export function formatDay(day: string | null | undefined): string {
  if (!day) return '—';
  const [year, month, date] = day.split('-');
  return `${date}/${month}/${year}`;
}

export function haulerName(hauler: { fantasyName: string | null; legalName: string | null }): string {
  return hauler.fantasyName || hauler.legalName || '—';
}

export function generatorName(generator: { name: string; fantasyName: string | null }): string {
  return generator.fantasyName || generator.name;
}

/**
 * Estabelecimento na movimentação de equipamento: o nome interno do gerador (MC-377). Grande
 * gerador reconhece a unidade por ele — o código da loja não está na razão social nem no fantasia.
 */
export function establishmentName(generator: { name: string; fantasyName: string | null; legalName?: string | null }): string {
  return generator.name || generator.fantasyName || generator.legalName || '—';
}

export function formatAddress(address: { street: string; number: string; neighborhood: string; city: string; state: string }): string {
  return `${address.street}, ${address.number} — ${address.neighborhood}, ${address.city}/${address.state}`;
}
